import {
  CHIME_GUEST_ID_HEX,
  parseChimeExternalUserId,
  type ChimeAttendeeKind,
} from '@unify/fhir-model/resources/chime-attendee';

const AVATAR_COLORS = ['blue', 'teal', 'grape', 'orange', 'cyan', 'indigo'] as const;

/** Up to two initials, handling extra whitespace and single-word display names. */
export function participantInitials(displayName: string | undefined): string {
  const parts = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts.at(-1)?.[0] ?? ''}`.toUpperCase();
}

/** Stable Mantine color for a participant across renders. */
export function participantAvatarColor(identity: string | undefined): (typeof AVATAR_COLORS)[number] {
  let hash = 0;
  for (const char of identity ?? '') {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

const FALLBACK_LABELS: Record<ChimeAttendeeKind, string> = {
  practitioner: 'Provider',
  patient: 'Client',
  guest: 'Guest',
};

/**
 * The name to show for an attendee (design Decision 39): every ExternalUserId carries its person's display name
 * (`<base id>#<name>`, a screen share adds `#content` and is named after its sharer), so every page labels tiles from
 * Chime's roster alone. Falls back to Provider / Client / Guest; undefined for an id the bots did not mint.
 */
export function chimeAttendeeDisplayName(externalUserId: string | undefined): string | undefined {
  const parsed = parseChimeExternalUserId(externalUserId);
  if (!parsed) return undefined;
  return parsed.name ?? FALLBACK_LABELS[parsed.kind];
}

/** A guest in the Chime roster: the 12-hex prefix of their handle, and the name Chime carries for them. */
export type PresentChimeGuest = { handlePrefix: string; name: string };

/**
 * Guests currently in the Chime roster (screen shares left out), one per handle prefix. Roster presence is the only
 * record of who is in the call. Match a full guest handle with {@link guestHandleMatches}.
 */
export function presentGuests(externalUserIds: Iterable<string>): PresentChimeGuest[] {
  const byPrefix = new Map<string, PresentChimeGuest>();
  for (const externalUserId of externalUserIds) {
    const parsed = parseChimeExternalUserId(externalUserId);
    if (parsed?.kind !== 'guest' || parsed.content || byPrefix.has(parsed.id)) continue;
    byPrefix.set(parsed.id, { handlePrefix: parsed.id, name: parsed.name ?? FALLBACK_LABELS.guest });
  }
  return [...byPrefix.values()];
}

/** True when a full guest handle (what `admit` / `remove-guest` take) is the one behind a roster prefix. */
export function guestHandleMatches(handle: string, handlePrefix: string): boolean {
  return handle.slice(0, CHIME_GUEST_ID_HEX) === handlePrefix;
}
